import { useState } from 'react'

import { Dialog, DialogContent, DialogHeader } from '#/shared/ui/dialog'
import { InfoRow } from '#/shared/ui/editable-info-card'
import { Input } from '#/shared/ui/input'
import { toast } from '#/shared/ui/sonner'
import { UnsavedChangesDialog } from '#/shared/ui/unsaved-changes-dialog'

import {
  passkeyCancelled,
  passkeyErrorMessage,
  useAddPasskey,
  useRemovePasskey,
  useRenamePasskey,
  type Passkey,
} from './passkeys'

const MAX_NAME = 64

/**
 * The one form both adding and renaming use: a label is all the admin types.
 *
 * Adding hands off to the browser's own passkey sheet after this closes — the
 * label is asked first so the passkey is recognisable in the list from the
 * moment it exists, rather than as "Passkey 3".
 */
function NameForm({
  title,
  submitLabel,
  initial,
  hint,
  pending,
  onSubmit,
}: {
  title: string
  submitLabel: string
  initial: string
  hint: string
  pending: boolean
  onSubmit: (name: string) => void
}) {
  const [name, setName] = useState(initial)

  return (
    <>
      <DialogHeader
        title={title}
        cancelLabel="Cancel"
        submitLabel={submitLabel}
        onSubmit={() => onSubmit(name.trim())}
        submitDisabled={pending || name.trim() === ''}
      />
      <div className="flex flex-col gap-3 p-5">
        <div className="rounded-md border border-gray-200 bg-white">
          <div className="edit-form-fields flex flex-col">
            <InfoRow label="Name">
              <Input
                inputSize="sm"
                autoFocus
                maxLength={MAX_NAME}
                placeholder="MacBook Touch ID"
                value={name}
                onChange={(event) => setName(event.target.value)}
              />
            </InfoRow>
          </div>
        </div>
        <p className="text-label text-gray-500">{hint}</p>
      </div>
    </>
  )
}

export function AddPasskeyDialog({
  open,
  onOpenChange,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
}) {
  const add = useAddPasskey()

  const start = (name: string) => {
    onOpenChange(false)
    add.mutate(
      { name },
      {
        onSuccess: (passkey) => toast.success(`${passkey.name} can now sign you in`),
        onError: (error) => {
          if (!passkeyCancelled(error)) toast.error(passkeyErrorMessage(error))
        },
      },
    )
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex max-w-md flex-col p-0">
        {open ? (
          <NameForm
            title="Add a passkey"
            submitLabel="Continue"
            initial=""
            hint="Name it after the device or key it lives on. Your browser asks for your fingerprint, face or PIN next."
            pending={add.isPending}
            onSubmit={start}
          />
        ) : null}
      </DialogContent>
    </Dialog>
  )
}

export function RenamePasskeyDialog({
  passkey,
  onClose,
}: {
  passkey: Passkey | null
  onClose: () => void
}) {
  const rename = useRenamePasskey()

  return (
    <Dialog open={passkey !== null} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="flex max-w-md flex-col p-0">
        {passkey ? (
          <NameForm
            title="Rename passkey"
            submitLabel="Save"
            initial={passkey.name}
            hint="Only the label changes; the passkey keeps working as before."
            pending={rename.isPending}
            onSubmit={(name) =>
              rename.mutate(
                { id: passkey.id, name },
                {
                  onSuccess: onClose,
                  onError: (error) => toast.error(passkeyErrorMessage(error)),
                },
              )
            }
          />
        ) : null}
      </DialogContent>
    </Dialog>
  )
}

export function RemovePasskeyDialog({
  passkey,
  onClose,
}: {
  passkey: Passkey | null
  onClose: () => void
}) {
  const remove = useRemovePasskey()

  return (
    <UnsavedChangesDialog
      open={passkey !== null}
      onOpenChange={(open) => !open && onClose()}
      title={`Remove ${passkey?.name ?? 'passkey'}?`}
      description="It can no longer sign you in to the console. Delete it from the device or password manager too."
      cancelLabel="Cancel"
      confirmLabel="Remove"
      onConfirm={() => {
        onClose()
        if (!passkey) return
        remove.mutate(
          { id: passkey.id },
          {
            onSuccess: () => toast.success(`${passkey.name} was removed`),
            onError: (error) => toast.error(passkeyErrorMessage(error)),
          },
        )
      }}
    />
  )
}
